import React, { ReactElement, useContext, useMemo } from "react";
import Logout from "@mui/icons-material/Logout";
import { FeedbackOutlined } from "@mui/icons-material";
import { useTextDomainContext, useUser } from "@/app/providers";
import { ProfileMenuItem } from "@/components";
import { useUpdateProfile, useUserRoles } from "@/hooks";
import { Divider, MenuList } from "@mui/material";
import { openFeedbackModal } from "@/features/feedback/utils";

interface UserRole {
  key: string;
  role: string;
}

/**
 * ProfileMenu component renders a menu for user profile options.
 * Displays available roles for switching and sign out option.
 */
export const ProfileMenu = (): ReactElement => {
  const { TextDomainContext } = useTextDomainContext();
  const { gettext } = useContext(TextDomainContext);
  const { userRoles, loading } = useUserRoles();
  const { user, setUser } = useUser();
  const { updateProfile } = useUpdateProfile();

  const effectiveRoles = useMemo(() => {
    const effectiveRolesObject: Record<string, string> = {};
    if (!loading && userRoles && Array.isArray(userRoles)) {
      userRoles.forEach((role: UserRole) => {
        if (role?.key !== user?.currentRoleKey) {
          effectiveRolesObject[role.key] = role.role;
        }
      });
    }
    return effectiveRolesObject;
  }, [loading, user?.currentRoleKey, userRoles]);

  const handleClick = async (key: string): Promise<void> => {
    if (key && user) {
      const variables = {
        currentRoleKey: key,
      };
      const updatedUser = {
        ...user,
        ...variables,
      };
      setUser(updatedUser);

      // Update profile and reload page to refresh all components
      await updateProfile(variables);
      window.location.reload();
    }
  };

  return (
    <MenuList>
      {loading && (
        <ProfileMenuItem
          icon={<Logout />}
          label={gettext("Loading")}
          codes={["allow-to-all", "everything-full", "everything-readonly"]}
        />
      )}

      {Object.entries(effectiveRoles).map(([key, role]) => (
        <ProfileMenuItem
          key={key}
          icon={<Logout />}
          label={gettext("Switch as") + " " + role}
          codes={["allow-to-all", "everything-full", "everything-readonly"]}
          desc=""
          onClick={() => handleClick(key)}
        />
      ))}

      {Object.entries(effectiveRoles).length > 0 && (
        <Divider color="primary.100" />
      )}
      <ProfileMenuItem
        icon={<FeedbackOutlined />}
        label={gettext("Send Feedback")}
        codes={["allow-to-all", "everything-full", "everything-readonly"]}
        desc=""
        onClick={() => openFeedbackModal()}
      />
      <ProfileMenuItem
        link={"/logout"}
        icon={<Logout />}
        label={gettext("Sign Out")}
        codes={["allow-to-all", "everything-full", "everything-readonly"]}
        desc=""
      />
    </MenuList>
  );
};
